import React, { useState, useEffect, useRef } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Textarea,
  VStack,
  Box,
  HStack,
  Text,
  useToast,
  Tag,
  Select,
  Flex,
  Progress,
  Circle,
} from '@chakra-ui/react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { CheckIcon } from '@chakra-ui/icons'
import { useUser } from '../../../../hooks/useUser'
import { api } from '../../../../services/api'
import { useQuery, useMutation, useQueryClient } from 'react-query'


// Esquema de validação para a primeira etapa
const primeiraEtapaSchema = z.object({
  id_produto: z.number().int().positive(),
  id_usuario: z.number().int().positive(),
  nome_produto: z.string().min(3, 'Nome do produto é obrigatório'),
  referencia: z.string().min(1, 'Referência é obrigatória'),
  caracteristicas: z.string().min(10, 'Características devem ter pelo menos 10 caracteres'),
  cod_grupo: z.number().int().positive('Código do grupo é obrigatório'),
  ncm: z.string().min(1, 'NCM é obrigatório'),
  cest: z.string().min(1, 'CEST é obrigatório'),
  bloqueia_fornecedor: z.enum(['S', 'N']).default('N'),
})

// Esquema de validação para a segunda etapa
const segundaEtapaSchema = z.object({
  codigo_produto: z.number().int().positive(),
  referencia: z.number().int().positive(),
  origem_produto: z.string().min(1, 'Origem do produto é obrigatória'),
  descricao_produto: z.string().min(3, 'Descrição do produto é obrigatória'),
  altura: z.string().min(1, 'Altura é obrigatória'),
  largura: z.string().min(1, 'Largura é obrigatória'),
  espessura: z.string().min(1, 'Espessura é obrigatória'),
  peso_bruto: z.string().min(1, 'Peso bruto é obrigatório'),
  outros_custos: z.string().default('0.00'),
  min_quantidade: z.string().min(1, 'Quantidade mínima é obrigatória'),
  max_batidas: z.string().min(1, 'Máximo de batidas é obrigatório'),
  min_prazo: z.string().min(1, 'Prazo mínimo é obrigatório'),
  multiplo_produto: z.string().min(1, 'Múltiplo do produto é obrigatório'),
  pedido_minimo: z.number().int().positive(),
  ativo_site: z.enum(['S', 'N']).default('S'),
  ativo: z.enum(['S', 'N']).default('S'),
})

// Etapas do Stepper
const steps = [
  { title: 'Informações Básicas', description: 'Dados básicos do produto' },
  { title: 'Informações Detalhadas', description: 'Medidas e propriedades' },
  { title: 'Fotos', description: 'Adicionar fotos do produto' },
]

function ModalEditarProdutoStepper({ isOpen, onClose, produto, isLoading, onSalvar }) {
  const toast = useToast()
  const { codigo_usuario } = useUser()
  const queryClient = useQueryClient()
  const [activeStep, setActiveStep] = useState(0)

  const [selectedFiles, setSelectedFiles] = useState([])
  const [previewUrls, setPreviewUrls] = useState([])
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef(null)

  // Formulário da primeira etapa
  const primeiraEtapaForm = useForm({
    resolver: zodResolver(primeiraEtapaSchema),
    defaultValues: {
      id_produto: produto?.id_produto ? Number(produto.id_produto) : 0, // Usar o código do produto da API
      id_usuario: Number(codigo_usuario),
      nome_produto: '',
      referencia: '',
      caracteristicas: '',
      cod_grupo: 0,
      ncm: '',
      cest: '',
      bloqueia_fornecedor: 'N',
    },
  })

  console.log(primeiraEtapaForm.formState.errors)

  // Formulário da segunda etapa
  const segundaEtapaForm = useForm({
    resolver: zodResolver(segundaEtapaSchema),
    defaultValues: {
      codigo_produto: 0,
      referencia: 0,
      origem_produto: '0',
      descricao_produto: '',
      altura: '',
      largura: '',
      espessura: '',
      peso_bruto: '',
      outros_custos: '0.00',
      min_quantidade: '1',
      max_batidas: '1',
      min_prazo: '10',
      multiplo_produto: '10',
      pedido_minimo: 10,
      ativo_site: 'S',
      ativo: 'S',
    },
  })

  // Atualizar com os valores do produto quando disponível
  useEffect(() => {
    if (produto) {
      console.log('Produto carregado:', produto)
      // Preencher formulário da primeira etapa
      primeiraEtapaForm.reset({
        id_produto: Number(produto.id_produto) || 0, // Usar o código do produto da API
        id_usuario: Number(codigo_usuario),
        nome_produto: produto.produto || '',
        referencia: produto.ref_fornecedor || '',
        caracteristicas: produto.observacoes || '',
        cod_grupo: Number(produto.cod_grupo) || 0,
        ncm: produto.ncm || '',
        cest: produto.cest || '',
        bloqueia_fornecedor: produto.bloqueia_fornecedor || 'N',
      })

      // Preencher formulário da segunda etapa
      segundaEtapaForm.reset({
        codigo_produto: Number(produto.codprod) || 0,
        referencia: Number(produto.ref_fornecedor) || 0,
        origem_produto: produto.origem_produto || '0',
        descricao_produto: produto.produto || '',
        altura: produto.altura || '',
        largura: produto.largura || '',
        espessura: produto.espessura || '',
        peso_bruto: produto.peso_bruto || '',
        outros_custos: produto.outros_custos || '0.00',
        min_quantidade: produto.qtd_min || '1',
        max_batidas: produto.max_batidas || '1',
        min_prazo: produto.prazo_min || '10',
        multiplo_produto: produto.multiplo || '10',
        pedido_minimo: Number(produto.pedido_minimo) || 10,
        ativo_site: produto.ativo_site || 'S',
        ativo: produto.ativo || 'S',
      })
    }
  }, [produto, codigo_usuario, primeiraEtapaForm, segundaEtapaForm])

  // Buscar categorias usando react-query
  const { data: categorias = [], isLoading: isLoadingCategorias } = useQuery(
    ['categorias'],
    async () => {
      try {
        const response = await api.get('/site/v2/detalhes-produto/listar-categorias')
        return response.data || []
      } catch (apiError) {
        console.error('API de categorias não disponível:', apiError)
        throw apiError
      }
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 60 * 24, // 24 horas
      onError: (error) => {
        console.error('Erro ao buscar categorias:', error)
        toast({
          title: 'Erro',
          description: 'Não foi possível carregar as categorias de produtos.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
      },
    }
  )

  // Mutation para salvar primeira etapa
  const salvarPrimeiraEtapaMutation = useMutation((data) => api.post('/produto/pre-cadastro/inserir-info-produto', data), {
    onSuccess: (response) => {
      toast({
        title: 'Sucesso',
        description: 'Informações básicas atualizadas com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })

      // Extrair o código do produto da resposta e configurar para a segunda etapa
      if (response?.data) {
        console.log('Código do produto recebido:', response.data)
        const codigoProduto = response.data
        segundaEtapaForm.setValue('codigo_produto', Number(codigoProduto))

        // Chamada direta para o endpoint com o código do produto retornado
        api
          .get(`/site/v2/detalhes-produto/listar-info-produtos/${codigoProduto}`)
          .then((response) => {
            const data = response.data
            console.log('Dados recebidos direto da API:', data)

            // Popular o formulário da segunda etapa com os dados da API
            segundaEtapaForm.setValue('codigo_produto', Number(data.codigo_produto) || 0)
            segundaEtapaForm.setValue('referencia', Number(data.referencia) || 0)
            segundaEtapaForm.setValue('origem_produto', data.orig_prod || '0')
            segundaEtapaForm.setValue('descricao_produto', data.descricao_produto || '')
            segundaEtapaForm.setValue('altura', data.altura || '')
            segundaEtapaForm.setValue('largura', data.largura || '')
            segundaEtapaForm.setValue('espessura', data.espessura || '')
            segundaEtapaForm.setValue('peso_bruto', data.peso_bruto || '')
            segundaEtapaForm.setValue('outros_custos', data.ad_outroscustos || '0.00')
            segundaEtapaForm.setValue('min_quantidade', data.ad_minqtd_site || '1')
            segundaEtapaForm.setValue('max_batidas', data.ad_maxbatidas || '1')
            segundaEtapaForm.setValue('min_prazo', data.ad_minprazoentrega || '10')
            segundaEtapaForm.setValue('multiplo_produto', data.ad_multiplo || '10')
            segundaEtapaForm.setValue('pedido_minimo', Number(data.ad_maxqtd_site) || 10)
            segundaEtapaForm.setValue('ativo_site', data.ad_ativosite || 'S')
            segundaEtapaForm.setValue('ativo', data.ativo || 'S')

            toast({
              title: 'Informações carregadas',
              description: 'Detalhes do produto carregados com sucesso',
              status: 'success',
              duration: 3000,
              isClosable: true,
            })
          })
          .catch((error) => {
            console.error('Erro ao buscar detalhes do produto:', error)
            toast({
              title: 'Erro',
              description: 'Não foi possível carregar os detalhes do produto. Você pode continuar preenchendo manualmente.',
              status: 'warning',
              duration: 5000,
              isClosable: true,
            })
          })
      } else {
        console.warn('Código do produto não encontrado na resposta da API')
        toast({
          title: 'Aviso',
          description: 'Não foi possível identificar o código do produto. Você pode continuar preenchendo manualmente.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        })
      }

      setActiveStep(1)
    },
    onError: (error) => {
      console.error('Erro ao salvar primeira etapa:', error)
      toast({
        title: 'Erro',
        description: error.response?.data?.message || 'Erro ao salvar informações básicas',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    },
  })

  // Mutation para salvar segunda etapa
  const salvarSegundaEtapaMutation = useMutation((data) => api.put('/produto/pre-cadastro/editar-info-produto', data), {
    onSuccess: () => {
      toast({
        title: 'Sucesso',
        description: 'Informações detalhadas atualizadas com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
      setActiveStep(2)
    },
    onError: (error) => {
      console.error('Erro ao salvar segunda etapa:', error)
      toast({
        title: 'Erro',
        description: error.response?.data?.message || 'Erro ao salvar informações detalhadas',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    },
  })

  // Mutation para enviar fotos
  const enviarFotosMutation = useMutation(
    (formData) =>
      api.post('/produto/pre-cadastro/adicionar-foto', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }),
    {
      onSuccess: () => {
        toast({
          title: 'Sucesso',
          description: 'Fotos adicionadas com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true,
        })

        // Invalidar queries para atualizar os dados
        queryClient.invalidateQueries(['produtos'])

        // Concluir o processo
        onSalvar()
        onClose()
      },
      onError: (error) => {
        console.error('Erro ao enviar fotos:', error)
        toast({
          title: 'Erro',
          description: error.response?.data?.message || 'Erro ao enviar fotos',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      },
    }
  )

  // Função para lidar com a submissão da primeira etapa
  const handleSubmitPrimeiraEtapa = async (data) => {
    console.log('Dados enviados na primeira etapa:', data)
    salvarPrimeiraEtapaMutation.mutate(data)
  }

  // Função para lidar com a submissão da segunda etapa
  const handleSubmitSegundaEtapa = async (data) => {
    salvarSegundaEtapaMutation.mutate(data)
  }

  // Função para lidar com a seleção de arquivos
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files)
    if (files.length === 0) return

    addFiles(files)
  }

  // Função para adicionar arquivos à lista
  const addFiles = (files) => {
    // Verifica se são imagens
    const imageFiles = files.filter(file => file.type.startsWith('image/'))
    
    if (imageFiles.length === 0) {
      toast({
        title: 'Aviso',
        description: 'Por favor, selecione apenas arquivos de imagem (JPG, PNG, GIF).',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }
    
    if (imageFiles.length > 0) {
      // Adicionar apenas até o limite máximo
      const maxFiles = 10
      const totalFiles = selectedFiles.length + imageFiles.length
      
      if (totalFiles > maxFiles) {
        toast({
          title: 'Aviso',
          description: `Você pode adicionar no máximo ${maxFiles} imagens. Apenas as primeiras ${maxFiles - selectedFiles.length} serão adicionadas.`,
          status: 'warning',
          duration: 4000,
          isClosable: true,
        })
        
        // Adicionar apenas até o limite
        const filesToAdd = imageFiles.slice(0, maxFiles - selectedFiles.length)
        setSelectedFiles(prevFiles => [...prevFiles, ...filesToAdd])
        
        // Criar URLs de preview
        const newUrls = filesToAdd.map((file) => URL.createObjectURL(file))
        setPreviewUrls(prevUrls => [...prevUrls, ...newUrls])
      } else {
        // Adicionar todos os arquivos
        setSelectedFiles(prevFiles => [...prevFiles, ...imageFiles])
        
        // Criar URLs de preview
        const newUrls = imageFiles.map((file) => URL.createObjectURL(file))
        setPreviewUrls(prevUrls => [...prevUrls, ...newUrls])
      }
    }
  }

  // Funções para drag and drop
  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!isDragging) setIsDragging(true)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    
    const files = Array.from(e.dataTransfer.files)
    addFiles(files)
  }

  // Função para abrir o seletor de arquivo ao clicar no dropzone
  const openFileSelector = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }
  
  // Função para remover uma imagem
  const removeImage = (index) => {
    // Revogar URL do objeto
    URL.revokeObjectURL(previewUrls[index])
    
    // Filtrar arrays para remover o item do índice especificado
    setPreviewUrls(prevUrls => prevUrls.filter((_, i) => i !== index))
    setSelectedFiles(prevFiles => prevFiles.filter((_, i) => i !== index))
  }

  // Função para enviar as fotos
  const handleSubmitFotos = async () => {
    if (selectedFiles.length === 0) {
      toast({
        title: 'Aviso',
        description: 'Selecione pelo menos uma foto para enviar',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    // Verificar se o código do produto está disponível
    const codigoProduto = segundaEtapaForm.getValues('codigo_produto')
    if (!codigoProduto) {
      console.error('Código do produto não encontrado no formulário')
      toast({
        title: 'Erro',
        description: 'Código do produto não encontrado. Não é possível enviar as fotos.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
      return
    }

    const formData = new FormData()
    
    // Usar o ID do produto da primeira etapa
    const idProduto = Number(produto?.id_produto) || 0
    formData.append('id_produto', String(idProduto))
    
    // Usar o código do produto que foi salvo no formulário da segunda etapa
    console.log('Enviando fotos com ID do produto:', idProduto)
    console.log('Enviando fotos com código do produto:', codigoProduto)
    formData.append('codigo_produto', String(codigoProduto))
    
    // Garantir que o ID do usuário seja incluído
    formData.append('id_usuario', String(codigo_usuario))

    selectedFiles.forEach((file) => {
      formData.append('imagem[]', file)
    })

    // Usar a mutação configurada para enviar as fotos
    enviarFotosMutation.mutate(formData)
  }

  // Função para voltar à etapa anterior
  const handlePreviousStep = () => {
    setActiveStep((prevStep) => prevStep - 1)
  }

  // Função para limpar os recursos ao fechar o modal
  const handleCloseModal = () => {
    // Limpar os previews das imagens
    previewUrls.forEach((url) => URL.revokeObjectURL(url))
    setPreviewUrls([])
    setSelectedFiles([])
    setActiveStep(0)
    onClose()
  }


  return (
    <Modal isOpen={isOpen} onClose={handleCloseModal} size="2xl">
      <ModalOverlay />
      <ModalContent borderRadius="sm">
        <ModalHeader fontSize="lg" fontWeight="bold" pb={2} borderBottomWidth="1px" borderBottomColor="gray.100">
          Editar Produto
          {produto?.ref_fornecedor && (
            <Tag size="md" colorScheme="blue" ml={2} mt={0} borderRadius="sm">
              {produto.ref_fornecedor}
            </Tag>
          )}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={4}>
          <VStack spacing={4} align="stretch">
            {/* Indicador de progresso */}
            <HStack spacing={4} align="center">
              <Text fontSize="sm" fontWeight="medium" color="gray.600">
                Etapa {activeStep + 1} de {steps.length}
              </Text>
              <Flex flex="1" alignItems="center">
                <Progress value={(activeStep / (steps.length - 1)) * 100} size="sm" colorScheme="blue" borderRadius="md" height="4px" width="100%" />
              </Flex>
              <HStack spacing={2}>
                {steps.map((step, index) => (
                  <Circle
                    key={index}
                    size="32px"
                    bg={index < activeStep ? 'green.500' : index === activeStep ? 'blue.500' : 'gray.200'}
                    color={index <= activeStep ? 'white' : 'gray.500'}
                    fontSize="sm"
                    fontWeight="bold"
                    textAlign="center"
                  >
                    {index < activeStep ? <CheckIcon boxSize={3} /> : index + 1}
                  </Circle>
                ))}
              </HStack>
            </HStack>

            <Box mb={4}>
              <Text fontSize="md" fontWeight="bold" color="blue.600">
                {steps[activeStep]?.title}
              </Text>
              <Text fontSize="sm" color="gray.500">
                {steps[activeStep]?.description}
              </Text>
            </Box>

            {/* Conteúdo da primeira etapa */}
            {activeStep === 0 && (
              <form onSubmit={primeiraEtapaForm.handleSubmit(handleSubmitPrimeiraEtapa)}>
                <VStack spacing={4} align="stretch">
                  <FormControl isInvalid={primeiraEtapaForm.formState.errors.nome_produto} isRequired>
                    <FormLabel>Nome do Produto</FormLabel>
                    <Input {...primeiraEtapaForm.register('nome_produto')} />
                    <FormErrorMessage>{primeiraEtapaForm.formState.errors.nome_produto?.message}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={primeiraEtapaForm.formState.errors.referencia} isRequired>
                    <FormLabel>Referência</FormLabel>
                    <Input {...primeiraEtapaForm.register('referencia')} />
                    <FormErrorMessage>{primeiraEtapaForm.formState.errors.referencia?.message}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={primeiraEtapaForm.formState.errors.caracteristicas} isRequired>
                    <FormLabel>Características</FormLabel>
                    <Textarea {...primeiraEtapaForm.register('caracteristicas')} rows={4} />
                    <FormErrorMessage>{primeiraEtapaForm.formState.errors.caracteristicas?.message}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={primeiraEtapaForm.formState.errors.cod_grupo} isRequired>
                    <FormLabel>Categoria do Produto</FormLabel>
                    <Select
                      {...primeiraEtapaForm.register('cod_grupo', {
                        valueAsNumber: true,
                      })}
                      placeholder="Selecione uma categoria"
                      isDisabled={isLoadingCategorias}
                    >
                      {categorias.length > 0 ? (
                        categorias.map((categoria) => (
                          <option key={categoria.codigo_grupo} value={Number(categoria.codigo_grupo)}>
                            {categoria.descricao_categoria}
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>
                          Nenhuma categoria disponível
                        </option>
                      )}
                    </Select>
                    <FormErrorMessage>{primeiraEtapaForm.formState.errors.cod_grupo?.message}</FormErrorMessage>
                  </FormControl>

                  <HStack spacing={4}>
                    <FormControl isInvalid={primeiraEtapaForm.formState.errors.ncm} isRequired>
                      <FormLabel>NCM</FormLabel>
                      <Input {...primeiraEtapaForm.register('ncm')} />
                      <FormErrorMessage>{primeiraEtapaForm.formState.errors.ncm?.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={primeiraEtapaForm.formState.errors.cest} isRequired>
                      <FormLabel>CEST</FormLabel>
                      <Input {...primeiraEtapaForm.register('cest')} />
                      <FormErrorMessage>{primeiraEtapaForm.formState.errors.cest?.message}</FormErrorMessage>
                    </FormControl>
                  </HStack>

                  <FormControl>
                    <FormLabel>Bloqueia Fornecedor</FormLabel>
                    <Select {...primeiraEtapaForm.register('bloqueia_fornecedor')}>
                      <option value="N">Não</option>
                      <option value="S">Sim</option>
                    </Select>
                  </FormControl>
                </VStack>
              </form>
            )}

            {/* Conteúdo da segunda etapa */}
            {activeStep === 1 && (
              <form onSubmit={segundaEtapaForm.handleSubmit(handleSubmitSegundaEtapa)}>
                <VStack spacing={4} align="stretch">
                  {/* Resto do formulário da etapa 2 */}
                  <HStack spacing={4}>
                    <FormControl isInvalid={segundaEtapaForm.formState.errors.origem_produto} isRequired>
                      <FormLabel>Origem do Produto</FormLabel>
                      <Select {...segundaEtapaForm.register('origem_produto')}>
                        <option value="0">0 - Nacional</option>
                        <option value="1">1 - Estrangeira (Importação direta)</option>
                        <option value="2">2 - Estrangeira (Adquirida no mercado interno)</option>
                        <option value="3">3 - Nacional (Mercadoria com conteúdo de importação superior a 40%)</option>
                        <option value="4">4 - Nacional (Produção conforme processos produtivos)</option>
                        <option value="5">5 - Nacional (Mercadoria com conteúdo de importação inferior ou igual a 40%)</option>
                        <option value="6">6 - Estrangeira (Importação direta, sem similar nacional)</option>
                        <option value="7">7 - Estrangeira (Adquirida no mercado interno, sem similar nacional)</option>
                        <option value="8">8 - Nacional (Mercadoria com conteúdo de importação superior a 70%)</option>
                      </Select>
                      <FormErrorMessage>{segundaEtapaForm.formState.errors.origem_produto?.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={segundaEtapaForm.formState.errors.codigo_produto} isRequired>
                      <FormLabel>Código do Produto</FormLabel>
                      <Input
                        {...segundaEtapaForm.register('codigo_produto', {
                          valueAsNumber: true,
                        })}
                        type="number"
                      />
                      <FormErrorMessage>{segundaEtapaForm.formState.errors.codigo_produto?.message}</FormErrorMessage>
                    </FormControl>
                  </HStack>

                  <FormControl isInvalid={segundaEtapaForm.formState.errors.descricao_produto} isRequired>
                    <FormLabel>Descrição do Produto</FormLabel>
                    <Input {...segundaEtapaForm.register('descricao_produto')} />
                    <FormErrorMessage>{segundaEtapaForm.formState.errors.descricao_produto?.message}</FormErrorMessage>
                  </FormControl>

                  <HStack spacing={4}>
                    <FormControl isInvalid={segundaEtapaForm.formState.errors.altura} isRequired>
                      <FormLabel>Altura (cm)</FormLabel>
                      <Input {...segundaEtapaForm.register('altura')} />
                      <FormErrorMessage>{segundaEtapaForm.formState.errors.altura?.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={segundaEtapaForm.formState.errors.largura} isRequired>
                      <FormLabel>Largura (cm)</FormLabel>
                      <Input {...segundaEtapaForm.register('largura')} />
                      <FormErrorMessage>{segundaEtapaForm.formState.errors.largura?.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={segundaEtapaForm.formState.errors.espessura} isRequired>
                      <FormLabel>Espessura (cm)</FormLabel>
                      <Input {...segundaEtapaForm.register('espessura')} />
                      <FormErrorMessage>{segundaEtapaForm.formState.errors.espessura?.message}</FormErrorMessage>
                    </FormControl>
                  </HStack>

                  <HStack spacing={4}>
                    <FormControl isInvalid={segundaEtapaForm.formState.errors.peso_bruto} isRequired>
                      <FormLabel>Peso Bruto (kg)</FormLabel>
                      <Input {...segundaEtapaForm.register('peso_bruto')} />
                      <FormErrorMessage>{segundaEtapaForm.formState.errors.peso_bruto?.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={segundaEtapaForm.formState.errors.outros_custos}>
                      <FormLabel>Outros Custos</FormLabel>
                      <Input {...segundaEtapaForm.register('outros_custos')} />
                      <FormErrorMessage>{segundaEtapaForm.formState.errors.outros_custos?.message}</FormErrorMessage>
                    </FormControl>
                  </HStack>

                  <HStack spacing={4}>
                    <FormControl isInvalid={segundaEtapaForm.formState.errors.min_quantidade} isRequired>
                      <FormLabel>Qtd. Mínima</FormLabel>
                      <Input {...segundaEtapaForm.register('min_quantidade')} />
                      <FormErrorMessage>{segundaEtapaForm.formState.errors.min_quantidade?.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={segundaEtapaForm.formState.errors.max_batidas} isRequired>
                      <FormLabel>Máx. Batidas</FormLabel>
                      <Select {...segundaEtapaForm.register('max_batidas')}>
                        {[...Array(10)].map((_, i) => (
                          <option key={i + 1} value={(i + 1).toString()}>
                            {i + 1}
                          </option>
                        ))}
                      </Select>
                      <FormErrorMessage>{segundaEtapaForm.formState.errors.max_batidas?.message}</FormErrorMessage>
                    </FormControl>
                  </HStack>

                  <HStack spacing={4}>
                    <FormControl isInvalid={segundaEtapaForm.formState.errors.min_prazo} isRequired>
                      <FormLabel>Prazo Mínimo</FormLabel>
                      <Select {...segundaEtapaForm.register('min_prazo')}>
                        {[...Array(10)].map((_, i) => (
                          <option key={i + 1} value={(i + 1).toString()}>
                            {i + 1}
                          </option>
                        ))}
                      </Select>
                      <FormErrorMessage>{segundaEtapaForm.formState.errors.min_prazo?.message}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={segundaEtapaForm.formState.errors.multiplo_produto} isRequired>
                      <FormLabel>Múltiplo</FormLabel>
                      <Input {...segundaEtapaForm.register('multiplo_produto')} />
                      <FormErrorMessage>{segundaEtapaForm.formState.errors.multiplo_produto?.message}</FormErrorMessage>
                    </FormControl>
                  </HStack>

                  <FormControl isInvalid={segundaEtapaForm.formState.errors.pedido_minimo} isRequired>
                    <FormLabel>Pedido Mínimo</FormLabel>
                    <Input
                      {...segundaEtapaForm.register('pedido_minimo', {
                        valueAsNumber: true,
                      })}
                      type="number"
                    />
                    <FormErrorMessage>{segundaEtapaForm.formState.errors.pedido_minimo?.message}</FormErrorMessage>
                  </FormControl>

                  <HStack spacing={4}>
                    <FormControl>
                      <FormLabel>Ativo</FormLabel>
                      <Select {...segundaEtapaForm.register('ativo')}>
                        <option value="S">Sim</option>
                        <option value="N">Não</option>
                      </Select>
                    </FormControl>

                    <FormControl>
                      <FormLabel>Ativo no Site</FormLabel>
                      <Select {...segundaEtapaForm.register('ativo_site')}>
                        <option value="S">Sim</option>
                        <option value="N">Não</option>
                      </Select>
                    </FormControl>
                  </HStack>
                </VStack>
              </form>
            )}

            {/* Conteúdo da terceira etapa */}
            {activeStep === 2 && (
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel>Fotos do produto</FormLabel>
                  
                  {/* Área de Drag and Drop */}
                  <Box
                    border="2px dashed"
                    borderColor={isDragging ? "blue.400" : "gray.300"}
                    borderRadius="md"
                    p={6}
                    bg={isDragging ? "blue.50" : "gray.50"}
                    transition="all 0.2s"
                    textAlign="center"
                    cursor="pointer"
                    onClick={openFileSelector}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    _hover={{ bg: "gray.100", borderColor: "gray.400" }}
                  >
                    <input
                      type="file"
                      ref={fileInputRef}
                      multiple
                      accept="image/*"
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                    />
                    <Text fontSize="32px" mb={2}>📷</Text>
                    <Text fontWeight="medium" mb={1}>
                      {isDragging ? "Solte as imagens aqui" : "Arraste e solte as imagens aqui"}
                    </Text>
                    <Text fontSize="sm" color="gray.500" mb={2}>
                      ou
                    </Text>
                    <Button size="sm" colorScheme="blue" fontWeight="medium">
                      Clique para selecionar
                    </Button>
                    <Text fontSize="xs" color="gray.500" mt={2}>
                      Apenas arquivos de imagem (JPG, PNG, GIF) - Máximo 10 imagens
                    </Text>
                  </Box>
                </FormControl>

                {previewUrls.length > 0 && (
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" mb={2}>
                      Preview das imagens ({previewUrls.length})
                    </Text>
                    <Flex flexWrap="wrap" gap={2}>
                      {previewUrls.map((url, index) => (
                        <Box 
                          key={index} 
                          width="100px" 
                          height="100px" 
                          borderWidth={1} 
                          borderRadius="md" 
                          overflow="hidden"
                          position="relative"
                        >
                          <img 
                            src={url} 
                            alt={`Preview ${index + 1}`} 
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                          />
                          <Button
                            position="absolute"
                            top="0"
                            right="0"
                            size="xs"
                            colorScheme="red"
                            borderRadius="full"
                            width="20px"
                            height="20px"
                            minW="20px"
                            p="0"
                            onClick={() => removeImage(index)}
                          >
                            &times;
                          </Button>
                        </Box>
                      ))}
                    </Flex>
                  </Box>
                )}
              </VStack>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter borderTopWidth="1px" borderTopColor="gray.100" pt={3} pb={3} justifyContent="space-between">
          <Box>
            {activeStep > 0 && (
              <Button
                onClick={handlePreviousStep}
                size="sm"
                variant="outline"
                isDisabled={salvarPrimeiraEtapaMutation.isLoading || salvarSegundaEtapaMutation.isLoading || enviarFotosMutation.isLoading}
              >
                Voltar
              </Button>
            )}
          </Box>
          <Box>
            <Button
              variant="outline"
              size="sm"
              mr={3}
              onClick={handleCloseModal}
              isDisabled={salvarPrimeiraEtapaMutation.isLoading || salvarSegundaEtapaMutation.isLoading || enviarFotosMutation.isLoading}
            >
              Cancelar
            </Button>

            {activeStep === 0 && (
              <Button
                colorScheme="blue"
                size="sm"
                onClick={primeiraEtapaForm.handleSubmit(handleSubmitPrimeiraEtapa)}
                isLoading={salvarPrimeiraEtapaMutation.isLoading}
                loadingText="Salvando..."
              >
                Próximo
              </Button>
            )}

            {activeStep === 1 && (
              <Button
                colorScheme="blue"
                size="sm"
                onClick={segundaEtapaForm.handleSubmit(handleSubmitSegundaEtapa)}
                isLoading={salvarSegundaEtapaMutation.isLoading}
                loadingText="Salvando..."
              >
                Próximo
              </Button>
            )}

            {activeStep === 2 && (
              <Button colorScheme="green" size="sm" onClick={handleSubmitFotos} isLoading={enviarFotosMutation.isLoading} loadingText="Enviando...">
                Concluir
              </Button>
            )}
          </Box>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default ModalEditarProdutoStepper
